import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import SanitizedGrid from "../../../components/SanitizedGrid";
import React, { useEffect, useState } from "react";
import { useField } from "react-final-form";
import { useDataProvider, useNotify } from "react-admin";
import {
  getFixedLocationDropOffBySchoolIds,
  getStudentAddressesByStudentIds,
  getStudents,
} from "../tools";
import ActionsMenu from "../../../components/ActionsMenu";
import ChangeStudentAddressButton from "./ChangeStudentAddressButton";
import RemoveStudentButton from "./RemoveStudentButton";
import AddStudentButton from "./AddStudentButton";
import CustomMuiDataGrid from "components/CustomMuiDataGrid";
import RemoveSelectedStudentButton from "./RemoveSelectedStudentButton";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";

const AdjustStudentAndAddressSection = () => {
  const dataColumns = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "fullName",
      headerName: "Full name",
      sortable: false,
      width: 320,
      valueGetter: ({ row }: any) => `${row.firstName} ${row.lastName}`,
    },
    {
      field: "fullAddress",
      headerName: "Full Address",
      sortable: false,
      width: 320,
      valueGetter: ({ row }: any) =>
        row.address
          ? `${row.address.blockNumber} ${row.address.street} ${row.address.postalCode}`
          : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      width: 320,
      renderCell: ({ id }: any) => {
        return (
          <ActionsMenu
            render={[
              <ChangeStudentAddressButton studentId={id} />,
              <RemoveStudentButton studentId={id} />,
            ]}
          />
        );
      },
    },
  ];

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [studentSelectedIds, setStudentSelectedIds] = useState([]);

  const provider = useDataProvider();
  const notify = useNotify();

  const busScheduleField = useField("busSchedule");
  const studentsField = useField("students");

  const handleResetSelectedValues = () => {
    setStudentSelectedIds([]);
  };

  const populateDataRows = async (busSchedule: IBusSchedule) => {
    let allStudents: IStudent[] = [];

    const isCcaSchedule = busSchedule.type === "cca";
    const isRegularSchedule =
      busSchedule.type === "to_school" || busSchedule.type === "from_school";

    await Promise.all(
      busSchedule.levels.map(async (level: ILevel) => {
        const results: any = await getStudents(
          Number(level.id),
          isCcaSchedule,
          isRegularSchedule,
          provider,
        );

        if (results.length > 0) {
          allStudents = [...allStudents, ...results];
        }
      }),
    );

    // Early return if no students found
    if (allStudents.length === 0) {
      return { studentsWithAddresses: [] };
    }

    const studentIds = allStudents.map((student) => student.id);
    const schoolId = allStudents[0].schoolId;

    const chunkSize = 100;
    let studentAddressResults: IStudentAddress[] = [];

    // Fetch student addresses in chunks
    for (let i = 0; i < studentIds.length; i += chunkSize) {
      const chunk = studentIds.slice(i, i + chunkSize);
      const addresses = await getStudentAddressesByStudentIds(chunk, provider);
      studentAddressResults.push(...(addresses as IStudentAddress[]));
    }

    // Fetch fixed location drop-off data (if needed)
    const fixedLocationDropOff = await getFixedLocationDropOffBySchoolIds(
      schoolId,
      provider,
    );
    studentAddressResults.push(...(fixedLocationDropOff as IStudentAddress[]));

    // Create a Map of student addresses for quick lookup
    const addressMap = new Map(
      studentAddressResults.map((addr) => [addr.studentId, addr]),
    );

    // Attach address to each student
    const studentsWithAddresses = allStudents.map((student) => ({
      ...student,
      address: addressMap.get(student.id) || null,
    }));

    return { studentsWithAddresses };
  };

  useEffect(() => {
    if (
      busScheduleField.input.value &&
      studentsField.input.value.length === 0
    ) {
      setIsLoading(true);

      const fetchAndLogData = async () => {
        try {
          const { studentsWithAddresses } = await populateDataRows(
            busScheduleField.input.value as IBusSchedule,
          );

          studentsField.input.onChange(studentsWithAddresses);
          setIsLoading(false);
        } catch (error) {
          notify("Error fetching or processing student data", "error");
          setIsLoading(false);
        }
      };

      fetchAndLogData();
    }
  }, [busScheduleField.input.value]);

  return (
    <SanitizedGrid container spacing={2}>
      {isLoading && (
        <Grid item xs={12} style={{ textAlign: "center", marginTop: "20px" }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: "10px" }}>
            Loading students...
          </Typography>
        </Grid>
      )}

      {!isLoading && (
        <>
          <Grid item xs={12}>
            <Alert severity="info">
              <AlertTitle>Info</AlertTitle>
              <Typography variant="body2">
                This is list of students that already included to the route
                plan. the selection box below is to select students that you
                want to remove from the route plan.
              </Typography>
            </Alert>
          </Grid>
          <Grid item xs={12} style={{ height: 800 }}>
            <CustomMuiDataGrid
              key={studentsField.input.value.length}
              sortModel={[
                {
                  field: "id",
                  sort: "asc",
                },
              ]}
              checkboxSelection
              disableSelectionOnClick
              selectionModel={studentSelectedIds}
              onSelectionModelChange={(newSelectionModel) => {
                setStudentSelectedIds(newSelectionModel);
              }}
              columnsToSearch={["id", "firstName", "lastName"]}
              rows={studentsField.input.value}
              columns={dataColumns}
              actions={
                <Box>
                  {studentSelectedIds.length > 0 && (
                    <RemoveSelectedStudentButton
                      studentIds={studentSelectedIds}
                      handleResetSelectedValues={handleResetSelectedValues}
                    />
                  )}

                  <AddStudentButton />
                </Box>
              }
            />
          </Grid>
        </>
      )}
    </SanitizedGrid>
  );
};

export default AdjustStudentAndAddressSection;
